import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";

const POLL_MS = 5 * 60 * 1000;

function dueText(d) {
  if (d < 0) return `${-d} day${d === -1 ? "" : "s"} overdue`;
  if (d === 0) return "due today";
  return d === 1 ? "due tomorrow" : `due in ${d} days`;
}

// Red blinking button (same idea as the CRM's "Biometric machine offline") for every login.
// Shows bills from Bill Tracking and the Meta ads token from 3 days before they are due.
// A bill stays until the owner marks it Paid; the token stays until the owner clicks Renewed.
export default function DueAlerts() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get("/alerts/due");
      setItems(res.data || []);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    load();
    const t = setInterval(load, POLL_MS);
    window.addEventListener("billing-updated", load);
    return () => {
      clearInterval(t);
      window.removeEventListener("billing-updated", load);
    };
  }, [user, load]);

  const act = async (item) => {
    setBusy(item.id);
    try {
      if (item.kind === "bill") {
        await api.post(`/billing-services/${item.id}/paid`);
        window.dispatchEvent(new Event("billing-updated"));
      } else {
        await api.post("/meta-ads/token-renewed");
        toast.success("Saved. The next alert comes 57 days from now.");
      }
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save");
    } finally {
      setBusy("");
    }
  };

  if (items.length === 0) return null;
  const first = items[0];
  const label = items.length === 1 ? `${first.name} ${dueText(first.days_left)}` : `${items.length} payments due soon`;

  return (
    <div className="relative">
      <style>{`
        @keyframes due-alert-blink {
          0%, 100% { background: #DC2626; box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.55); }
          50% { background: #991B1B; box-shadow: 0 0 0 7px rgba(220, 38, 38, 0); }
        }
        .due-alert-btn { animation: due-alert-blink 2.2s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .due-alert-btn { animation: none; background: #DC2626; } }
      `}</style>
      <button
        type="button"
        data-testid="due-alert-button"
        className="due-alert-btn rounded-md px-3 py-1.5 text-sm font-extrabold text-white"
        onClick={() => setOpen((o) => !o)}
      >
        ⚠ {label}
      </button>
      {open && (
        <div
          data-testid="due-alert-panel"
          className="absolute right-0 top-[calc(100%+8px)] z-50 w-80 max-w-[90vw] rounded-xl border-2 border-red-600 bg-[#141414] p-3 shadow-2xl"
        >
          <div className="text-sm font-extrabold text-red-400 mb-1">Due soon</div>
          {items.map((it) => (
            <div key={it.id} className="py-2 border-t border-white/10 text-xs">
              <div className="font-extrabold text-white text-sm">{it.name}</div>
              <div className="text-slate-400 mt-0.5">
                {dueText(it.days_left)} ({it.due}){it.purpose ? ` · ${it.purpose}` : ""}
              </div>
              {it.kind === "meta_token" && isOwner && (
                <div className="text-slate-500 mt-1">
                  Make a new token, update META_ADS_TOKEN and META_ADS_TOKEN_EXPIRES on Railway (adiyashauto back), Deploy, then press Renewed.
                </div>
              )}
              {isOwner && (
                <button
                  type="button"
                  data-testid={`due-alert-done-${it.id}`}
                  onClick={() => act(it)}
                  disabled={busy === it.id}
                  className="mt-2 w-full flex items-center justify-center gap-2 rounded-md bg-orange-600 hover:bg-orange-700 text-white font-semibold text-sm py-1.5 disabled:opacity-60"
                >
                  {busy === it.id && <Loader2 className="h-4 w-4 animate-spin" />}
                  {it.kind === "bill" ? "Mark as Paid" : "Renewed"}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
